import { useLiveQuery } from 'dexie-react-hooks';
import { Lock, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { dailyTotals, milkSaleAmount } from '@dairy/domain';
import {
  birrInputValue,
  litersInputValue,
  newId,
  PAYMENT_METHODS,
  tryParseBirr,
  tryParseLiters,
  type PaymentMethod,
  type SaleType,
} from '@dairy/shared';
import { useIsOwner, useProfile, useSettings } from '@/auth/useSession';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DateField } from '@/components/DateField';
import { LitersInput, MoneyInput } from '@/components/inputs';
import { toast } from '@/components/toast';
import { Badge, Button, Card, Field, Input, PageHeader, Segmented, Select } from '@/components/ui';
import { errorMessage } from '@/components/errors';
import { db } from '@/db/db';
import { isLocked, remove, saveMany, type Change } from '@/db/repo';
import { rememberLastUsed, useLastUsed, useToday } from '@/db/hooks';
import { useFormat } from '@/lib/format';

/** Screen 16 — Record sale: Milk · Animal · Other (IN-2, IN-5). */
export function SaleFormPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { t } = useTranslation();
  const f = useFormat();
  const s = useSettings();
  const profile = useProfile();
  const isOwner = useIsOwner();
  const today = useToday();
  const navigate = useNavigate();
  const last = useLastUsed();
  const existing = useLiveQuery(() => (id ? db.sales.get(id) : undefined), [id]);
  const buyers = useLiveQuery(() => db.buyers.toArray(), []) ?? [];
  const animals = useLiveQuery(() => db.animals.toArray(), []) ?? [];
  const [type, setType] = useState<SaleType>((params.get('type') as SaleType) ?? 'MILK');
  const [date, setDate] = useState(today);
  const [buyerId, setBuyerId] = useState(params.get('buyerId') ?? '');
  const [liters, setLiters] = useState('');
  const [price, setPrice] = useState('');
  const [amount, setAmount] = useState('');
  const [animalId, setAnimalId] = useState(params.get('animalId') ?? '');
  const [description, setDescription] = useState('');
  const [paidNow, setPaidNow] = useState('');
  const [method, setMethod] = useState<PaymentMethod>((last.paymentMethod as PaymentMethod) ?? 'CASH');
  const [error, setError] = useState<string | null>(null);
  const [warn, setWarn] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Today's saleable milk not yet sold (IN-2 default).
  const dayData = useLiveQuery(async () => {
    const [records, usage, sales] = await Promise.all([
      db.milkRecords.where('date').equals(date).toArray(),
      db.dailyMilkUsage.where('date').equals(date).toArray(),
      db.sales.where('date').equals(date).filter((x) => x.type === 'MILK' && x.id !== id).toArray(),
    ]);
    return { saleable: dailyTotals(records, usage, date).saleableMl, sold: sales.reduce((a, x) => a + (x.milkMl ?? 0), 0) };
  }, [date, id]);

  useEffect(() => {
    if (existing) {
      setType(existing.type);
      setDate(existing.date);
      setBuyerId(existing.buyerId ?? '');
      setLiters(litersInputValue(existing.milkMl));
      setPrice(birrInputValue(existing.unitPriceSantim));
      setAmount(birrInputValue(existing.amountSantim));
      setAnimalId(existing.animalId ?? '');
      setDescription(existing.description ?? '');
    } else if (!buyerId && last.buyerId) setBuyerId(last.buyerId);
  }, [existing]);

  const buyer = buyers.find((b) => b.id === buyerId);
  useEffect(() => {
    if (type === 'MILK' && buyer && (!existing || existing.buyerId !== buyer.id)) setPrice(birrInputValue(buyer.pricePerLiterSantim));
  }, [buyer, type, existing]);
  useEffect(() => {
    if (!existing && type === 'MILK' && !liters && dayData && dayData.saleable - dayData.sold > 0) setLiters(litersInputValue(dayData.saleable - dayData.sold));
  }, [dayData, type]);

  const ml = tryParseLiters(liters);
  const priceP = tryParseBirr(price);
  const milkAmount = useMemo(
    () => (type === 'MILK' && ml.ok && ml.ml && priceP.ok && priceP.santim ? milkSaleAmount(ml.ml, priceP.santim) : null),
    [type, ml, priceP],
  );
  const locked = !!existing && !isOwner && (existing.createdBy !== profile.userId || isLocked(existing, profile, s));
  const priceLocked = !isOwner;

  const submit = async (force = false) => {
    setError(null);
    let record: Record<string, unknown>;
    if (type === 'MILK') {
      if (!buyerId) return setError(t('money.errors.buyer'));
      if (!ml.ok || !ml.ml || ml.ml < 100) return setError(t('money.errors.liters'));
      if (!priceP.ok || !priceP.santim) return setError(t('money.errors.price'));
      if (!force && dayData && dayData.sold + ml.ml > dayData.saleable) {
        setWarn(t('money.oversellWarning', { liters: f.liters(dayData.saleable - dayData.sold) }));
        return;
      }
      record = { type, date, buyerId, milkMl: ml.ml, unitPriceSantim: priceP.santim, amountSantim: milkAmount, animalId: null, description: null };
    } else {
      const a = tryParseBirr(amount);
      if (!a.ok || !a.santim) return setError(t('money.amountRequired'));
      if (type === 'ANIMAL' && !animalId) return setError(t('money.errors.animal'));
      if (type === 'OTHER' && !description.trim()) return setError(t('money.errors.description'));
      record = { type, date, buyerId: buyerId || null, milkMl: null, unitPriceSantim: null, amountSantim: a.santim, animalId: type === 'ANIMAL' ? animalId : null, description: description.trim() || null };
    }
    const saleId = existing?.id ?? newId();
    const changes: Change[] = [{ entity: 'sale', op: 'upsert', record: { ...(existing ?? {}), ...record, id: saleId } }];
    const paid = tryParseBirr(paidNow);
    if (!existing && buyerId && paid.ok && paid.santim) {
      if (paid.santim > (record.amountSantim as number)) return setError(t('money.errors.paidTooMuch'));
      changes.push({ entity: 'payment', op: 'upsert', record: { id: newId(), buyerId, date, amountSantim: paid.santim, method, reference: null, saleId } });
    }
    try {
      await saveMany(changes);
      if (buyerId) await rememberLastUsed('buyerId', buyerId);
      await rememberLastUsed('paymentMethod', method);
      toast(navigator.onLine ? t('common.saved') : t('common.savedOffline'));
      navigate(buyerId ? `/buyers/${buyerId}` : '/money', { replace: true });
    } catch (e) {
      setError(errorMessage(t, e));
    }
  };

  return (
    <div className="mx-auto max-w-lg space-y-4 pb-8">
      <PageHeader title={existing ? t('money.editSale') : t('money.recordSale')} />
      {locked ? (
        <p className="flex items-center gap-2 rounded-xl bg-stone-200 p-3">
          <Lock className="h-4 w-4" /> {t('common.lockedAskOwner')}
        </p>
      ) : null}
      <fieldset disabled={locked} className="space-y-4">
        {!existing ? (
          <Segmented
            value={type}
            onChange={setType}
            options={[
              { value: 'MILK', label: t('money.saleType.MILK') },
              { value: 'ANIMAL', label: t('money.saleType.ANIMAL') },
              { value: 'OTHER', label: t('money.saleType.OTHER') },
            ]}
          />
        ) : (
          <Badge>{t(`money.saleType.${type}`)}</Badge>
        )}
        <Card className="space-y-4">
          <Field label={t('common.date')}>
            <DateField value={date} max={today} onChange={setDate} />
          </Field>
          <Field label={type === 'MILK' ? t('money.buyer') : t('money.buyerOptional')}>
            <Select value={buyerId} onChange={(e) => setBuyerId(e.target.value)}>
              <option value="">{type === 'MILK' ? t('money.chooseBuyer') : t('money.noBuyer')}</option>
              {buyers.filter((b) => b.active || b.id === buyerId).map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </Select>
          </Field>
          {type === 'MILK' ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t('money.liters')} hint={dayData ? t('money.saleableHint', { liters: f.liters(Math.max(0, dayData.saleable - dayData.sold)) }) : undefined}>
                  <LitersInput value={liters} onChange={(e) => setLiters(e.target.value)} />
                </Field>
                <Field label={t('money.unitPrice')} hint={priceLocked ? t('money.priceLocked') : undefined}>
                  <MoneyInput value={price} onChange={(e) => setPrice(e.target.value)} disabled={priceLocked} />
                </Field>
              </div>
              <div className="rounded-xl bg-stone-100 p-3 text-lg">
                {t('money.amount')}: <span className="font-bold tabular-nums">{milkAmount === null ? '—' : f.birr(milkAmount)}</span>
              </div>
            </>
          ) : (
            <>
              {type === 'ANIMAL' ? (
                <Field label={t('money.animal')}>
                  <Select value={animalId} onChange={(e) => setAnimalId(e.target.value)}>
                    <option value="">{t('events.chooseAnimal')}</option>
                    {animals.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.tag} {a.name ?? ''}
                      </option>
                    ))}
                  </Select>
                </Field>
              ) : null}
              <Field label={type === 'OTHER' ? t('money.description') : t('money.descriptionOptional')}>
                <Input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} placeholder={type === 'OTHER' ? t('money.otherExample') : undefined} />
              </Field>
              <Field label={t('money.amount')}>
                <MoneyInput value={amount} onChange={(e) => setAmount(e.target.value)} />
              </Field>
              {!buyerId ? <p className="text-sm text-stone-600">{t('money.cashNote')}</p> : null}
            </>
          )}
        </Card>
        {!existing && buyerId ? (
          <Card className="space-y-3">
            <Field label={t('money.paidNow')}>
              <MoneyInput value={paidNow} onChange={(e) => setPaidNow(e.target.value)} />
            </Field>
            <Field label={t('money.method')}>
              <Select value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
                {PAYMENT_METHODS.map((m) => (
                  <option key={m} value={m}>
                    {t(`money.methodType.${m}`)}
                  </option>
                ))}
              </Select>
            </Field>
          </Card>
        ) : null}
        {error ? <p className="text-red-700">{error}</p> : null}
        <Button size="lg" className="w-full" onClick={() => void submit()}>
          {t('common.save')}
        </Button>
        {existing ? (
          <Button variant="ghost" className="w-full" onClick={() => setConfirmDelete(true)}>
            <Trash2 className="h-5 w-5" /> {t('common.delete')}
          </Button>
        ) : null}
      </fieldset>
      <ConfirmDialog open={!!warn} onOpenChange={(o) => !o && setWarn(null)} title={t('common.areYouSure')} confirmLabel={t('common.saveAnyway')} onConfirm={() => submit(true)}>
        <p>{warn}</p>
      </ConfirmDialog>
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t('money.deleteSale')}
        danger
        confirmLabel={t('common.delete')}
        onConfirm={async () => {
          if (!existing) return;
          await remove('sale', existing.id);
          navigate(-1);
        }}
      />
    </div>
  );
}
